/** Analytics: GA4 + Google Ads gtag (loaded in public/index.html) + UTM capture. */

const AW_ID = "AW-18376177070";
const GA4_ID = "G-J7CKLB1DTW";

const CONVERSIONS = {
  purchase: `${AW_ID}/hNfkCPGu1d0cEK7ruLpE`,
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid"];
const UTM_STORE_KEY = "passadc_utm";

function safeGtag(...args) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  try { window.gtag(...args); } catch { /* noop */ }
}

/** Capture UTM/gclid from current URL, persist first-touch in localStorage. */
export function captureUtmFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v;
    });
    if (Object.keys(found).length === 0) return null;
    const existing = getStoredUtm();
    // First-touch attribution: only overwrite if there's nothing stored yet
    if (!existing) {
      const payload = {
        ...found,
        landing_page: window.location.pathname + window.location.search,
        referrer: document.referrer || "",
        captured_at: new Date().toISOString(),
      };
      localStorage.setItem(UTM_STORE_KEY, JSON.stringify(payload));
      return payload;
    }
    return existing;
  } catch {
    return null;
  }
}

export function getStoredUtm() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(UTM_STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearStoredUtm() {
  try { localStorage.removeItem(UTM_STORE_KEY); } catch { /* noop */ }
}

/** Fire a GA4 event. Automatically appends stored UTM so we can attribute in GA4. */
export function trackEvent(eventName, params = {}) {
  const utm = getStoredUtm() || {};
  const enriched = {
    ...params,
    // Snake_case params GA4 will show in reports
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    utm_term: utm.utm_term,
    utm_content: utm.utm_content,
    gclid: utm.gclid,
    send_to: GA4_ID,
  };
  // Strip undefined values
  Object.keys(enriched).forEach((k) => enriched[k] === undefined && delete enriched[k]);
  safeGtag("event", eventName, enriched);
}

/** Fire a Google Ads Purchase conversion (deduped by transaction_id). */
export function trackPurchaseConversion({ value, currency = "AUD", transactionId }) {
  const key = `gads_purchase_${transactionId || "anon"}`;
  if (transactionId && typeof sessionStorage !== "undefined") {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  }
  safeGtag("event", "conversion", {
    send_to: CONVERSIONS.purchase,
    value: typeof value === "number" && value > 0 ? value : 1.0,
    currency: currency || "AUD",
    transaction_id: transactionId || "",
  });
  // Also fire GA4 purchase event for funnel reporting
  trackEvent("pro_purchase", { value, currency, transaction_id: transactionId });
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Freemium v2 paywall analytics. Fires the GA4 event AND mirrors it to the
 * backend so the admin Tracking page can report counts.
 * event: "view" | "click"; wall: daily_cap | lesson_lock | results_fix | mock_breakdown */
export function trackPaywall(event, wall) {
  trackEvent(event === "click" ? "paywall_click" : "paywall_view", { wall });
  try {
    fetch(`${BACKEND}/api/tracking/paywall`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ event, wall }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* noop */ }
}

/** GA4 trial_unlocked. reason: "readiness" | "20_questions" */
export function trackTrialUnlocked(reason) {
  trackEvent("trial_unlocked", { reason });
}
